// Per-column presentation, keyed by canonical column id. Holds the label
// fallback, header i18n key, sizing and cell renderer that call sites used to
// declare inline. Visibility is deliberately NOT here: the same column is
// hidden unconditionally on one screen and conditionally on another, so hiding
// is view policy rather than a property of the column.
import type {
  MeqCellContext,
  MeqColumnDef,
  MeqRowData,
} from "~/components/MeqTable/types";

import { toColumnId, toColumnName } from "./columnIds";

// Resolves an i18n key to its display string. Narrowed to the single call shape
// the registry needs so it does not depend on the full i18next TFunction.
type TranslateFn = (key: string) => string;

/** Overrides merged over the column def `prepareQueryResultForTable` builds. */
export type ColumnParams = Partial<MeqColumnDef<MeqRowData>>;

export type ColumnPresentationContext = {
  /** Friendly plant labels keyed by the raw LOCATION code each row carries. */
  locationLabels?: ReadonlyMap<string, string>;
  /** Registry keys are namespace-qualified, so any bound `t` resolves them.
   *  Entries with a `headerKey` keep their generated label when it is absent. */
  t?: TranslateFn;
};

type ColumnPresentation = {
  headerKey?: string;
  /** Label used when `common:columnNames.<NAME>` has no entry. */
  labelFallback?: string;
  tableParams?:
    | ColumnParams
    | ((context: ColumnPresentationContext) => ColumnParams);
};

const LOCATION_NAME = toColumnName("location");

const COLUMN_PRESENTATION = {
  barcode: { tableParams: { size: 340, minSize: 340 } },
  carcasses_missed: { labelFallback: "Carcases missed low scan score" },
  kph: { labelFallback: "KPH" },
  location: {
    headerKey: "camera:summaryOfImages.location",
    tableParams: ({ locationLabels }: ColumnPresentationContext) => ({
      Cell: ({ row }: MeqCellContext<MeqRowData>) => {
        // Display only — the raw code stays on the row because the image URLs
        // are built from it.
        const code = row.original[LOCATION_NAME] as string | undefined;
        return (code && locationLabels?.get(code)) || code || "";
      },
    }),
  },
  percentage_of_not_probed: { labelFallback: "% Scan score below 20" },
  photo_datetime: { tableParams: { maxSize: 250 } },
  potential_of_hydrogen: { labelFallback: "pH" },
  pyg: { labelFallback: "PYG" },
  yg: { labelFallback: "YG" },
} satisfies Record<string, ColumnPresentation>;

export type ColumnId = keyof typeof COLUMN_PRESENTATION;

const PRESENTATION_BY_ID: ReadonlyMap<string, ColumnPresentation> = new Map(
  Object.entries(COLUMN_PRESENTATION),
);

export const getColumnLabelFallback = (name: string): string | undefined =>
  PRESENTATION_BY_ID.get(toColumnId(name))?.labelFallback;

/** Builds `prepareQueryResultForTable`'s `columnParams`, keyed by result column
 *  name. Ids with nothing to override are omitted. */
export const buildColumnParams = (
  ids: readonly ColumnId[],
  context: ColumnPresentationContext = {},
): Record<string, ColumnParams> =>
  Object.fromEntries(
    ids.flatMap((id) => {
      const presentation = PRESENTATION_BY_ID.get(id);
      if (!presentation) return [];

      const { headerKey, tableParams } = presentation;
      const params: ColumnParams = {
        ...(headerKey && context.t ? { header: context.t(headerKey) } : {}),
        ...(typeof tableParams === "function"
          ? tableParams(context)
          : tableParams),
      };

      return Object.keys(params).length > 0 ? [[toColumnName(id), params]] : [];
    }),
  );
